import type { ReactNode } from 'react';
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { EditorHTML } from '@duke-hq/ui/components/DukeTiptapEditor/EditorHTML';
import { Trans } from '@lingui/react/macro';
import {
  ENGLISH_LANGUAGE_CODE,
  languageDir,
} from '@duke-hq/shared-constants/courseTranslationLanguages';
import { useReadyByLanguage } from '../../components/translations/useReadyByLanguage';
import { PreviewLanguagePicker } from '../../components/ReactFlow/tiptap/simulation/dBlock/PreviewLanguagePicker';
import type { PreviewVersion, PreviewLanguageControls } from './PreviewModal';
import { FileUrlsProvider } from '@duke-hq/svc-files-ui/useTiptapFileSrcResolver';

type UnitGuidePreviewProps = {
  resourceId: string;
  lastPublishedVersionId: number | null;
  version: PreviewVersion;
  languageControls?: PreviewLanguageControls;
  versionToggle?: ReactNode;
};

export function UnitGuidePreview({
  resourceId,
  lastPublishedVersionId,
  version,
  languageControls,
  versionToggle,
}: UnitGuidePreviewProps) {
  const language = languageControls?.value ?? ENGLISH_LANGUAGE_CODE;
  const showPublished = version === 'published' && lastPublishedVersionId !== null;
  const showTranslated = showPublished && language !== ENGLISH_LANGUAGE_CODE;

  const resourceQuery = trpcAdmin.resources.byId.useQuery(resourceId, {
    enabled: !showPublished,
  });

  // Also enabled when translated, to back the English fallback.
  const publishedVersionQuery = trpcAdmin.resourceVersions.byId.useQuery(
    lastPublishedVersionId ?? 0,
    {
      enabled: showPublished,
    },
  );

  // Availability per language, so the picker can grey out ones that aren't ready.
  const readyByLanguage = useReadyByLanguage(
    lastPublishedVersionId,
    'PAGE',
    languageControls !== undefined,
  );

  // Null content means "serve English", so fall back to the published version.
  const translatedQuery = trpcAdmin.contentTranslations.getTranslatedContent.useQuery(
    { publishedVersionId: lastPublishedVersionId ?? 0, resourceKind: 'PAGE', language },
    { enabled: showTranslated },
  );

  const content = showTranslated
    ? (translatedQuery.data?.content ?? publishedVersionQuery.data?.content)
    : showPublished
      ? publishedVersionQuery.data?.content
      : resourceQuery.data?.draftVersion?.content;

  const fileUrls = showTranslated
    ? translatedQuery.data?.content != null
      ? translatedQuery.data.fileUrls
      : publishedVersionQuery.data?.fileUrls
    : showPublished
      ? publishedVersionQuery.data?.fileUrls
      : resourceQuery.data?.fileUrls;

  const isLoading = showTranslated
    ? translatedQuery.isLoading || publishedVersionQuery.isLoading
    : showPublished
      ? publishedVersionQuery.isLoading
      : resourceQuery.isLoading;

  return (
    <Box maxW="800px" mx="auto">
      <Flex align="center" justify="space-between" gap="3" mb="4">
        {versionToggle ?? <Box />}
        {languageControls ? (
          <PreviewLanguagePicker
            value={languageControls.value}
            onChange={languageControls.onChange}
            readyByLanguage={readyByLanguage}
          />
        ) : null}
      </Flex>

      {isLoading ? (
        <Flex justify="center" align="center" minH="200px">
          <Spinner />
        </Flex>
      ) : content ? (
        <Box dir={languageDir(language)}>
          <FileUrlsProvider fileUrls={fileUrls}>
            <EditorHTML content={content} />
          </FileUrlsProvider>
        </Box>
      ) : (
        <Box textAlign="center" py="8">
          <Text color="fg.muted">
            <Trans>No content available for this version.</Trans>
          </Text>
        </Box>
      )}
    </Box>
  );
}
